import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiResponse, PageableApiResponse } from '../../api-response';
import { DisplayTechnology, SoftwareUpdate, UploadSoftwareUpdateRequest } from '../models/software-update.model';
import { AssignableDevicesData, AssignableDevicesQuery } from '../models/assignable-device.model';
import { BatchAssignmentData, BatchAssignmentRequest } from '../models/batch-assignment.model';
import {
  RolloutDeviceRow,
  RolloutDevicesQuery,
  RolloutSummaryData,
  RolloutTenantsQuery,
  RolloutTenantSummary,
} from '../models/rollout.model';
import { BaseApiService } from '../../../core/base-api.service';

export interface SoftwareUpdatesListResponse {
  status: string;
  message: string;
  data: SoftwareUpdate[];
}

@Injectable({ providedIn: 'root' })
export class SoftwareUpdateService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.coreApiUrl}/api/v1/software-updates`;

  getAll(technology?: DisplayTechnology, page = 0, size = 20): Observable<SoftwareUpdatesListResponse> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (technology) params = params.set('technology', technology);
    return this.http.get<SoftwareUpdatesListResponse>(this.baseUrl, { params });
  }

  getById(id: string): Observable<ApiResponse<SoftwareUpdate>> {
    return this.http.get<ApiResponse<SoftwareUpdate>>(`${this.baseUrl}/${id}`);
  }

  upload(req: UploadSoftwareUpdateRequest): Observable<ApiResponse<SoftwareUpdate>> {
    const formData = new FormData();
    formData.append('file', req.file);
    formData.append('name', req.name);
    formData.append('version', req.version);
    formData.append('displayTechnology', req.displayTechnology);
    if (req.description) formData.append('description', req.description);
    if (req.md5) formData.append('md5', req.md5);
    return this.http.post<ApiResponse<SoftwareUpdate>>(this.baseUrl, formData);
  }

  delete(id: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }

  getSasDownloadUrl(softwareUpdateId: string): Observable<string> {
    return this.http.get<{ url: string }>(`${this.baseUrl}/download/${softwareUpdateId}`)
      .pipe(map(({ url }) => url));
  }

  /**
   * Cross-tenant device search/select source for the Assign devices tab.
   * Pagination is per tenant group, not global — see AssignableDevicesData.
   */
  getAssignableDevices(
    updateId: string,
    query: AssignableDevicesQuery = {},
  ): Observable<ApiResponse<AssignableDevicesData>> {
    let params = new HttpParams()
      .set('page', query.page ?? 0)
      .set('size', query.size ?? 20);
    if (query.q) params = params.set('q', query.q);
    if (query.tenantIds?.length) params = params.set('tenantIds', query.tenantIds.join(','));
    if (query.currentVersion) params = params.set('currentVersion', query.currentVersion);
    if (query.hideAssigned !== undefined) params = params.set('hideAssigned', query.hideAssigned);

    return this.http.get<ApiResponse<AssignableDevicesData>>(
      `${this.baseUrl}/${updateId}/assignable-devices`,
      { params },
    );
  }

  /**
   * Pushes this update to an explicit set of devices across one or more
   * tenants in a single call. Per-device results come back even on a 200 —
   * a mismatched clientId/tenant pairing fails just that entry, not the
   * whole request. See BatchAssignmentData.
   */
  assignBatch(
    updateId: string,
    request: BatchAssignmentRequest,
  ): Observable<ApiResponse<BatchAssignmentData>> {
    return this.http.post<ApiResponse<BatchAssignmentData>>(
      `${this.baseUrl}/${updateId}/assignments`,
      request,
    );
  }

  /**
   * Current-state rollout snapshot for the Rollout tab's summary tiles and
   * per-tenant progress bars — not a history (see RolloutSummaryData).
   */
  getRolloutSummary(updateId: string, tenantId?: string): Observable<ApiResponse<RolloutSummaryData>> {
    let params = new HttpParams();
    if (tenantId) params = params.set('tenantId', tenantId);
    return this.http.get<ApiResponse<RolloutSummaryData>>(`${this.baseUrl}/${updateId}/rollout`, { params });
  }

  /**
   * The Rollout tab's drill-down device table — globally paginated (flat
   * list), unlike assignable-devices' per-tenant pagination.
   */
  getRolloutDevices(
    updateId: string,
    query: RolloutDevicesQuery = {},
  ): Observable<PageableApiResponse<RolloutDeviceRow>> {
    let params = new HttpParams()
      .set('page', query.page ?? 0)
      .set('size', query.size ?? 20);
    if (query.status && query.status !== 'all') params = params.set('status', query.status);
    if (query.q) params = params.set('q', query.q);
    if (query.tenantId) params = params.set('tenantId', query.tenantId);

    return this.http.get<PageableApiResponse<RolloutDeviceRow>>(
      `${this.baseUrl}/${updateId}/rollout/devices`,
      { params },
    );
  }

  /**
   * Re-attempts every device the server considers eligible for retry for
   * this update (currently `failed`, or stuck mid-flight past its server-
   * side threshold) — optionally scoped to one tenant. Eligibility is
   * entirely server-resolved; this never sends a device list. Response
   * shape is identical to assignBatch()'s, by design — same result-handling
   * code covers both.
   */
  retryFailed(updateId: string, tenantId?: string): Observable<ApiResponse<BatchAssignmentData>> {
    return this.http.post<ApiResponse<BatchAssignmentData>>(
      `${this.baseUrl}/${updateId}/assignments/retry`,
      tenantId ? { tenantId } : {},
    );
  }

  /**
   * The Rollout tab's By-tenant table — paginated and sorted server-side
   * (holds up at hundreds of tenants, unlike the old embedded, unpaginated
   * `tenants[]` on the summary response). Same per-tenant row shape as that
   * summary endpoint; both read the same underlying aggregate so the two
   * can't disagree.
   */
  getRolloutTenants(
    updateId: string,
    query: RolloutTenantsQuery = {},
  ): Observable<PageableApiResponse<RolloutTenantSummary>> {
    let params = new HttpParams()
      .set('page', query.page ?? 0)
      .set('size', query.size ?? 20);
    if (query.sort) params = params.set('sort', query.sort);

    return this.http.get<PageableApiResponse<RolloutTenantSummary>>(
      `${this.baseUrl}/${updateId}/rollout/tenants`,
      { params },
    );
  }
}
